import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ContestService } from '../services/ContestService';
import { SingAlongService, SingAlongPurchase } from '../services/SingAlongService';
import { trackPurchase } from '../lib/analytics';
import { Loader as Loader2, Check, CircleAlert as AlertCircle, ArrowRight } from 'lucide-react';

export const ContestSetup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchase, setPurchase] = useState<SingAlongPurchase | null>(null);
  const [isSubscription, setIsSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [contestName, setContestName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [purchaseTracked, setPurchaseTracked] = useState(false);

  useEffect(() => {
    if (purchase && searchParams.get('success') === 'true' && !purchaseTracked) {
      setPurchaseTracked(true);
      const name = purchase.pass?.name || 'Contest Plan';
      const priceCents = purchase.pass?.price_cents || 0;
      trackPurchase(name, priceCents, 'USD', purchase.id);
    }
  }, [purchase, searchParams, purchaseTracked]);

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (!user || !user.email_confirmed_at) {
        navigate('/sing-along/signup', { replace: true });
        return;
      }

      try {
        const hasSub = await ContestService.hasActiveSubscription();
        setIsSubscription(hasSub);

        if (hasSub) {
          const subPurchase = await ContestService.getActiveSubscriptionPurchase();
          setPurchase(subPurchase as SingAlongPurchase | null);
        } else {
          const existingContests = await ContestService.getUserContests();
          if (existingContests.length > 0) {
            setPurchase(null);
          } else {
            const activePurchase = await SingAlongService.getActivePurchase();
            if (!activePurchase) { navigate('/contests/plans', { replace: true }); return; }
            if (activePurchase.league_id) { navigate(`/contests/manage/${activePurchase.league_id}`, { replace: true }); return; }
            setPurchase(activePurchase);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const generateSlug = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 50);

  useEffect(() => {
    if (!contestName) { setSlug(''); setSlugAvailable(null); return; }
    const newSlug = generateSlug(contestName);
    setSlug(newSlug);

    if (newSlug.length < 3) { setSlugAvailable(null); return; }

    setSlugChecking(true);
    const timer = setTimeout(async () => {
      try {
        const available = await ContestService.checkSlugAvailable(newSlug);
        setSlugAvailable(available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setSlugChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [contestName]);

  const canSubmit = !!contestName.trim() && slug.length >= 3 && slugAvailable === true && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    try {
      await ContestService.createContestLeague({
        purchaseId: isSubscription ? undefined : purchase?.id,
        name: contestName.trim(),
        slug,
      });
      navigate('/contests/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contest');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-16">
      <div className="absolute inset-0 bg-gradient-to-br from-sky-600/10 via-transparent to-emerald-600/10 pointer-events-none" />

      <div className="relative max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-black mb-3">Name Your Contest</h1>
          <p className="text-white/50">Give your contest a name and a public URL. You'll customize branding, sponsors, rules, songs, and participants next.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
            <h2 className="font-bold text-lg">Contest Name</h2>
            <div>
              <input
                type="text"
                value={contestName}
                onChange={(e) => setContestName(e.target.value.slice(0, 60))}
                placeholder="e.g. Summer Sing-Off 2026"
                className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-sky-500/50 transition"
              />
              {slug && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-white/40">URL: /league/{slug}</span>
                  {slugChecking && <Loader2 size={14} className="animate-spin text-white/40" />}
                  {!slugChecking && slugAvailable === true && <Check size={14} className="text-green-400" />}
                  {!slugChecking && slugAvailable === false && (
                    <span className="text-red-400 text-xs">Already taken</span>
                  )}
                </div>
              )}
              {contestName && slug.length < 3 && (
                <p className="mt-2 text-xs text-amber-300">Name must produce a URL of at least 3 characters.</p>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-4 bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-bold text-lg rounded-xl hover:shadow-lg hover:shadow-sky-500/25 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                Continue to Editor
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContestSetup;
